import React from 'react';
import { motion } from 'framer-motion';
import { ToolCard } from './ToolCard';
import { CategoryNav } from './CategoryNav';
import { useTools } from '../hooks/useTools';
import { Tool } from '../types';
// Fallback data - matches original Tool interface
const FALLBACK_TOOLS: Tool[] = [{
  id: '7',
  name: 'Perplexity',
  description: 'AI-powered search engine that provides direct answers with citations.',
  logo: 'https://upload.wikimedia.org/wikipedia/commons/1/1d/Perplexity_AI_logo.svg',
  url: 'https://perplexity.ai',
  category: 'Other',
  icon: '🔍',
  pricing: 'Freemium',
  priceDetails: 'Free, Pro $20/mo',
  isNew: true
}, {
  id: '8',
  name: 'Suno',
  description: 'Generate high-quality songs and music from simple text prompts.',
  logo: 'https://suno.com/images/logo.svg',
  url: 'https://suno.com',
  category: 'Audio',
  icon: '🎵',
  pricing: 'Freemium',
  priceDetails: 'Free, Pro $8/mo, Premier $24/mo',
  isNew: true
}, {
  id: '9',
  name: 'V0.dev',
  description: 'Generative UI system by Vercel that builds React components from text.',
  logo: 'https://v0.dev/favicon.ico',
  url: 'https://v0.dev',
  category: 'Development',
  icon: '⚡',
  pricing: 'Freemium',
  priceDetails: 'Free, Premium $20/mo'
}, {
  id: '10',
  name: 'ElevenLabs',
  description: 'Realistic text-to-speech and voice cloning with emotional range.',
  logo: 'https://elevenlabs.io/favicon.ico',
  url: 'https://elevenlabs.io',
  category: 'Audio',
  icon: '🎙️',
  pricing: 'Freemium',
  priceDetails: 'Free, Starter $5/mo, Creator $22/mo'
}, {
  id: '11',
  name: 'Cursor',
  description: 'AI-first code editor built for pair programming with AI.',
  logo: 'https://cursor.sh/favicon.ico',
  url: 'https://cursor.sh',
  category: 'Development',
  icon: '💻',
  pricing: 'Freemium',
  priceDetails: 'Free, Pro $20/mo',
  trending: true
}, {
  id: '12',
  name: 'Notion AI',
  description: 'AI writing assistant integrated directly into your Notion workspace.',
  logo: 'https://notion.so/favicon.ico',
  url: 'https://notion.so/product/ai',
  category: 'Productivity',
  icon: '📝',
  pricing: 'Paid',
  priceDetails: 'Add-on $10/mo'
}];
interface NewToolsSectionProps {
  activeCategory: string;
  setActiveCategory: (category: string) => void;
}
export function NewToolsSection({
  activeCategory,
  setActiveCategory
}: NewToolsSectionProps) {
  const {
    tools,
    loading
  } = useTools();
  // Map database tools to UI format, or use fallback
  const allTools = tools.length > 0 ? tools.map(dbTool => ({
    id: dbTool.id,
    name: dbTool.name,
    description: dbTool.description,
    logo: dbTool.logo_url,
    url: dbTool.website_url,
    category: dbTool.category as any,
    icon: '🔧',
    pricing: 'Freemium' as const,
    priceDetails: dbTool.pricing_tiers?.map((t: any) => `${t.name} ${t.price}`).join(', ')
  })) : FALLBACK_TOOLS;
  const filteredTools = activeCategory === 'all' ? allTools : allTools.filter(tool => tool.category === activeCategory);
  return <section className="py-16 px-4 max-w-7xl mx-auto" data-id="element-122">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4" data-id="element-123">
        <div data-id="element-124">
          <h2 className="text-3xl md:text-4xl text-white mb-3 font-subtitle" data-id="element-125">
            More AI Tools
          </h2>
          <p className="text-gray-400 text-sm max-w-xl" data-id="element-126">
            Discover the latest AI innovations across every category. Updated
            daily.
          </p>
        </div>
      </div>

      <CategoryNav activeCategory={activeCategory} setActiveCategory={setActiveCategory} data-id="element-127" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mt-8" data-id="element-128">
        {filteredTools.map((tool, index) => <motion.div key={tool.id} initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5,
        delay: index * 0.05
      }} className="h-full" data-id="element-129">
            <ToolCard tool={tool} data-id="element-130" />
          </motion.div>)}
      </div>

      {filteredTools.length === 0 && <div className="text-center py-20 text-gray-500" data-id="element-131">
          No tools found in this category yet.
        </div>}
    </section>;
}